import { Injectable, inject } from '@angular/core';
import uniq from 'lodash-es/uniq';
import isEqual from 'lodash-es/isEqual';
import { DateTime } from 'luxon';
import { CustomFiltersService } from '../../services/custom-filters.service';
import { DateHandlingService } from '../../services/date-handling.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { ptGrids, presetDateValues } from '../../lib/rb-grid-lib';
@Injectable({
  providedIn: 'root',
})
export class RbGridService {
  private cstmFltrSrvc = inject(CustomFiltersService);
  private dateSvc = inject(DateHandlingService);
  private ss = inject(SessionStorageService);

  selectedDropdowns: Record<string, any> = {};
  selectedDates: Record<string, any> = {};
  selectedPresets: Record<string, any> = {};

  generateSearchStringQuery(filterObj, filterGroup: Array<string>) {
    const searchStringQuery: Record<string, any> = {};

    if (
      searchStringQuery[filterObj['field']] &&
      filterGroup.includes(filterObj['field'])
    ) {
      searchStringQuery[filterObj['field']].searchString = filterObj['value'];
    } else if (
      !searchStringQuery[filterObj['field']] &&
      filterGroup.includes(filterObj['field'])
    ) {
      searchStringQuery[filterObj['field']] = {
        searchString: filterObj['value'],
      };
    }

    return searchStringQuery[filterObj['field']];
  }
  generateEqualSearchStringQuery(filterObj, filterGroup: Array<string>) {
    const searchStringQuery: Record<string, any> = {};

    if (
      searchStringQuery[filterObj['field']] &&
      filterGroup.includes(filterObj['field'])
    ) {
      searchStringQuery[filterObj['field']].equals = filterObj['value'];
    } else if (
      !searchStringQuery[filterObj['field']] &&
      filterGroup.includes(filterObj['field'])
    ) {
      searchStringQuery[filterObj['field']] = {
        equals: filterObj['value'],
      };
    }

    return searchStringQuery[filterObj['field']];
  }

  generateDropdownQuery(
    filterObj,
    filterGroup: Array<string>,
    dropdownOptions
  ) {
    if (
      this.selectedDropdowns[filterObj['field']] &&
      (filterGroup.includes(filterObj['field']) ||
        Object.keys(dropdownOptions).includes(filterObj['field']))
    ) {
      if (Array.isArray(filterObj['value'])) {
        this.selectedDropdowns[filterObj['field']].selected = uniq(
          this.selectedDropdowns[filterObj['field']].selected.concat(
            filterObj['value']
          )
        );
      } else {
        this.selectedDropdowns[filterObj['field']].selected.push(
          filterObj['value']
        );
      }
    } else if (
      !this.selectedDropdowns[filterObj['field']] &&
      (filterGroup.includes(filterObj['field']) ||
        Object.keys(dropdownOptions).includes(filterObj['field']))
    ) {
      if (Array.isArray(filterObj['value'])) {
        this.selectedDropdowns[filterObj['field']] = {
          selected: filterObj['value'],
        };
      } else {
        this.selectedDropdowns[filterObj['field']] = {
          selected: [filterObj['value']],
        };
      }
    }

    return this.selectedDropdowns[filterObj['field']];
  }

  generateDateQuery(filterObj, filterGroup: Array<string>) {
    if (
      this.selectedDates[filterObj.field] &&
      filterGroup.includes(filterObj.field)
    ) {
      this.selectedDates[filterObj.field][filterObj.operator] =
        DateTime.fromJSDate(new Date(filterObj.value)).toUTC().toISO();
    } else if (
      !this.selectedDates[filterObj.field] &&
      filterGroup.includes(filterObj.field)
    ) {
      this.selectedDates[filterObj['field']] = {};
      this.selectedDates[filterObj['field']][filterObj.operator] =
        DateTime.fromJSDate(new Date(filterObj.value)).toUTC().toISO();
    }

    return this.selectedDates[filterObj['field']];
  }

  generatePresetDateQuery(
    filterObj,
    filterGroup: Array<string>,
    relatedDate,
    gridName
  ) {
    if (
      !this.selectedPresets[filterObj['field']] &&
      filterGroup.includes(filterObj['field'])
    ) {
      this.selectedPresets[filterObj['field']] = {};
      if (presetDateValues.includes(filterObj['value'])) {
        this.cstmFltrSrvc.calculatePresetRange(filterObj, this.selectedPresets);
      } else {
        const startFilter = relatedDate.find(
          setFilter =>
            setFilter['field'] === filterObj['field'] &&
            setFilter['operator'] === 'start'
        );
        const endFilter = relatedDate.find(
          setFilter =>
            setFilter['field'] === filterObj['field'] &&
            setFilter['operator'] === 'end'
        );
        if (!startFilter || !endFilter) {
          return undefined;
        }
        if (ptGrids.includes(gridName)) {
          if (startFilter) {
            this.selectedPresets[filterObj['field']]['start'] =
              this.dateSvc.toTruncatedIso(startFilter['value']);
          }
          if (endFilter) {
            this.selectedPresets[filterObj['field']]['end'] =
              this.dateSvc.toTruncatedIso(endFilter['value']);
          }
        } else {
          if (startFilter) {
            this.selectedPresets[filterObj['field']]['start'] =
              startFilter['value'];
          }
          if (endFilter) {
            this.selectedPresets[filterObj['field']]['end'] =
              endFilter['value'];
          }
        }
      }
    } else if (
      this.selectedPresets[filterObj['field']] &&
      filterGroup.includes(filterObj['field'])
    ) {
      if (presetDateValues.includes(filterObj['value'])) {
        this.cstmFltrSrvc.calculatePresetRange(filterObj, this.selectedPresets);
      } else {
        relatedDate = { filters: relatedDate.filters ?? relatedDate };
        if (ptGrids.includes(gridName)) {
          this.selectedPresets[filterObj['field']]['start'] =
            this.dateSvc.toTruncatedIso(
              relatedDate.filters.find(
                setFilter =>
                  setFilter['field'] === filterObj['field'] &&
                  setFilter['operator'] === 'start'
              )['value']
            );
          this.selectedPresets[filterObj['field']]['end'] =
            this.dateSvc.toTruncatedIso(
              relatedDate.filters.find(
                setFilter =>
                  setFilter['field'] === filterObj['field'] &&
                  setFilter['operator'] === 'end'
              )['value']
            );
        } else {
          this.selectedPresets[filterObj['field']]['start'] =
            relatedDate.filters.find(
              setFilter =>
                setFilter['field'] === filterObj['field'] &&
                setFilter['operator'] === 'start'
            )['value'];
          this.selectedPresets[filterObj['field']]['end'] =
            relatedDate.filters.find(
              setFilter =>
                setFilter['field'] === filterObj['field'] &&
                setFilter['operator'] === 'end'
            )['value'];
        }
      }
    }

    return this.selectedPresets[filterObj['field']];
  }

  generateValueRangeQuery(queryData, filterObj, filterGroup: Array<string>) {
    const valueRangeQuery = queryData;
    if (
      valueRangeQuery.search[filterObj['field']] &&
      filterGroup.includes(filterObj['field']) &&
      filterObj['operator'] === 'gte'
    ) {
      valueRangeQuery.search[filterObj['field']]['equalGreaterThan'] =
        filterObj['value'];
    } else if (
      valueRangeQuery.search[filterObj['field']] &&
      filterGroup.includes(filterObj['field']) &&
      filterObj['operator'] === 'lte'
    ) {
      valueRangeQuery.search[filterObj['field']]['equalLessThan'] =
        filterObj['value'];
    } else if (
      !valueRangeQuery.search[filterObj['field']] &&
      filterGroup.includes(filterObj['field']) &&
      filterObj['operator'] === 'gte'
    ) {
      valueRangeQuery.search[filterObj['field']] = {
        equalGreaterThan: filterObj['value'],
      };
    } else if (
      !valueRangeQuery.search[filterObj['field']] &&
      filterGroup.includes(filterObj['field']) &&
      filterObj['operator'] === 'lte'
    ) {
      valueRangeQuery.search[filterObj['field']] = {
        equalLessThan: filterObj['value'],
      };
    }

    return valueRangeQuery;
  }

  generateExceptionsQuery(
    queryData,
    filterObj,
    filterGroup: Array<string>,
    gridName
  ) {
    const exceptionsQuery = queryData;
    if (
      !exceptionsQuery.search[filterObj['field']] &&
      typeof filterObj['value'] === 'object' &&
      !filterGroup.includes(filterObj['field'])
    ) {
      exceptionsQuery.search[filterObj['field']] = {
        selected: filterObj['value'],
      };
    } else if (
      !exceptionsQuery.search[filterObj['field']] &&
      typeof filterObj['value'] !== 'object' &&
      !filterGroup.includes(filterObj['field'])
    ) {
      if (
        ['CaseSearch', 'CopyCase', 'RepBehavior', 'CaseExplorer'].includes(
          gridName
        ) &&
        ['repCode', 'userId', 'unitId', 'caseCompleted'].includes(
          filterObj['field']
        )
      ) {
        exceptionsQuery.search[filterObj['field']] = {
          equals: filterObj['value'],
        };
      } else {
        exceptionsQuery.search[filterObj['field']] = {
          selected: [filterObj['value']],
        };
      }
    }

    return exceptionsQuery;
  }

  parseDefaultView(defaultView, headers, filter) {
    const parsedFilters = filter;
    defaultView.filter.forEach(col => {
      let fieldType: string;
      headers.forEach(gridCol => {
        if (
          gridCol.DataField === Object.keys(col)[0] &&
          gridCol.externalFilter
        ) {
          fieldType = gridCol.externalFilter.controlType;
        }
      });
      if (fieldType === 'multiselect') {
        parsedFilters.filters.push({
          field: Object.keys(col)[0],
          operator: 'contains',
          value: Object.values(col)[0],
        });
      } else if (
        fieldType === 'range' ||
        (fieldType === 'presetRange' &&
          Object.values(col)[0]['start'] &&
          Object.values(col)[0]['end'])
      ) {
        parsedFilters.filters.push({
          field: Object.keys(col)[0],
          operator: 'start',
          value: Object.values(col)[0]['start'],
        });
        parsedFilters.filters.push({
          field: Object.keys(col)[0],
          operator: 'end',
          value: Object.values(col)[0]['end'],
        });
        parsedFilters.filters.push({
          field: Object.keys(col)[0],
          operator: 'contains',
          value: 'custom',
        });
      } else if (
        fieldType === 'presetRange' &&
        Object.values(col)[0]['selected'] &&
        (Object.values(col)[0]['selected'][0].includes('days') ||
          Object.values(col)[0]['selected'][0].includes('months'))
      ) {
        // Calculate time difference
        const calculateTime =
          Object.values(col)[0]['selected'][0].split(' ')[0];
        const calculateUnit =
          Object.values(col)[0]['selected'][0].split(' ')[1];
        const timeDiffObject = {};
        timeDiffObject[calculateUnit] = calculateTime;
        const calculatedStartDate = DateTime.now()
          .minus(timeDiffObject)
          .startOf('day')
          .toUTC()
          .toISO();
        parsedFilters.filters.push({
          field: Object.keys(col)[0],
          operator: 'start',
          value: calculatedStartDate,
        });
        parsedFilters.filters.push({
          field: Object.keys(col)[0],
          operator: 'end',
          value: DateTime.now().endOf('day').toUTC().toISO(),
        });
      } else if (
        fieldType === 'presetRange' &&
        Object.values(col)[0]['selected'] &&
        Object.values(col)[0]['selected'][0].includes('td')
      ) {
        let startVal;
        switch (Object.values(col)[0]['selected'][0]) {
          case 'ytd':
            startVal = DateTime.now().startOf('year').toUTC().toISO();
            break;
          case 'mtd':
            startVal = DateTime.now().startOf('month').toUTC().toISO();
            break;
          case '12mtd':
            startVal = DateTime.now()
              .minus({ years: 1 })
              .startOf('month')
              .toUTC()
              .toISO();
            break;
        }

        parsedFilters.filters.push({
          field: Object.keys(col)[0],
          operator: 'start',
          value: startVal,
        });
        parsedFilters.filters.push({
          field: Object.keys(col)[0],
          operator: 'end',
          value: DateTime.now().toUTC().toISO(),
        });
      } else {
        parsedFilters.filters.push({
          field: Object.keys(col)[0],
          operator: 'contains',
          value: Object.values(col)[0],
        });
      }
    });

    return parsedFilters;
  }

  disableSearch(passedFilter, disableData, nonEqualFlag?) {
    const { gridName, conditionalSearch, headers, lastFilter } = disableData;
    let disableSearchButton = false;

    const startFilters = passedFilter.filters.filter(
      filter => filter['operator'] === 'start'
    );

    if (startFilters.length > 0) {
      startFilters.map(filter => {
        const endFilter = passedFilter.filters.find(
          checkFilter =>
            checkFilter['field'] === filter['field'] &&
            checkFilter['operator'] === 'end'
        );

        if (!endFilter) {
          disableSearchButton = true;
        }
      });
    }

    if (conditionalSearch && headers) {
      if (gridName === 'IWProductShelf') {
        return !passedFilter.filters.find(
          filter => filter['field'] === 'ProgramName'
        );
      } else {
        headers
          .filter(
            header =>
              header.externalFilter &&
              header.externalFilter.filterGroup !== 'additional'
          )
          .map(header => {
            if (
              !passedFilter.filters.find(
                filter => filter['field'] === header.DataField
              )
            ) {
              disableSearchButton = true;
            }
          });
      }
      if (!disableSearchButton && gridName === 'RepBehavior' && !nonEqualFlag) {
        // must be a unique search. Only rep behavior for now,
        // change or add to if needed.
        if (lastFilter?.filters) {
          lastFilter.filters.sort((a, b) => {
            if (a['field'] < b['field']) {
              return -1;
            }
            if (a['field'] > b['field']) {
              return 1;
            }
            return 0;
          });
        }
        if (passedFilter?.filters) {
          passedFilter.filters.sort((a, b) => {
            if (a['field'] < b['field']) {
              return -1;
            }
            if (a['field'] > b['field']) {
              return 1;
            }
            return 0;
          });
        }
        if (isEqual(lastFilter, passedFilter)) {
          disableSearchButton = true;
        }
      }
      if (gridName === 'CaseExplorer') {
        disableSearchButton = false;
        if (
          !passedFilter.filters.find(
            filter => filter['field'] === 'solutionId'
          ) ||
          !passedFilter.filters.find(
            filter => filter['field'] === 'savedTime'
          ) ||
          !passedFilter.filters.find(filter => filter['field'] === 'unitId')
        ) {
          disableSearchButton = true;
        }
      }
    }

    if (gridName === 'CarrierPolicyDetails') {
      disableSearchButton = false;

      if (
        !passedFilter.filters.find(filter => filter['field'] === 'IssueDate')
      ) {
        disableSearchButton = true;
      } else {
        const startFilter = passedFilter.filters.find(
          filter =>
            filter['field'] === 'IssueDate' && filter['operator'] === 'start'
        );

        if (
          startFilter &&
          !['mtd', 'ytd', '12mtd', '7 days', '30 days', '90 days'].includes(
            startFilter['value']
          ) &&
          !passedFilter.filters.find(
            filter =>
              filter['field'] === 'IssueDate' && filter['operator'] === 'end'
          )
        ) {
          disableSearchButton = true;
        }
      }
    }

    return disableSearchButton;
  }

  flattenAndRemoveDuplicates(filter: any): any {
    const uniqueFilters = new Set<string>();
    let flattenedFilters: any[] = [];

    function traverseFilters(filters: any[]) {
      for (const item of filters) {
        if (item.filters && Array.isArray(item.filters)) {
          // Recursively traverse nested filters
          traverseFilters(item.filters);
        } else {
          // Stringify the filter to check for uniqueness
          const filterKey = JSON.stringify(item);
          if (!uniqueFilters.has(filterKey)) {
            uniqueFilters.add(filterKey);
            flattenedFilters.push(item);
          }
        }
      }
    }

    // Start traversal with the top-level filters
    if (filter.filters && Array.isArray(filter.filters)) {
      traverseFilters(filter.filters);
    }
    const flattenedValues = flattenedFilters.map(flatItem => flatItem.value);
    flattenedFilters
      .filter(filterItem => filterItem.operator === 'selected')
      .forEach(filterItem => {
        filterItem.value.forEach(subFilterItem => {
          if (!flattenedValues.includes(subFilterItem))
            flattenedFilters.push({
              field: filterItem.field,
              operator: 'contains',
              value: subFilterItem,
            });
        });
      });
    flattenedFilters = flattenedFilters.filter(
      filterItem => filterItem.operator !== 'selected'
    );
    return flattenedFilters;
  }

  deDupObjectArray(data) {
    const uniqueSet = new Set();

    const processFilter = filter => {
      // If the filter has sub-filters, recursively process them
      if (filter.filters && Array.isArray(filter.filters)) {
        filter.filters = this.deDupObjectArray(filter.filters);
      }
      return filter;
    };

    return data.filter(filter => {
      const processedFilter = processFilter(filter);
      const key = JSON.stringify(processedFilter);
      if (uniqueSet.has(key)) {
        return false;
      }
      uniqueSet.add(key);
      return true;
    });
  }

  getSolutionOptions(gridName?) {
    const solutions: Array<{ label: string; value: string }> = [];
    const globals = this.ss.get('globals');

    if (globals) {
      globals['Modules'].forEach((solution: string) => {
        switch (solution.toLowerCase()) {
          case 'aw':
            solutions.push({
              label: 'Annuity Wizard',
              value: solution.toLowerCase(),
            });
            break;
          case 'iw':
            solutions.push({
              label: 'Investment Wizard',
              value: solution.toLowerCase(),
            });
            break;
          case 'li':
            solutions.push({
              label: 'Life Insurance Wizard',
              value: solution.toLowerCase(),
            });
            break;
          case 'pp':
            solutions.push({
              label: 'Product Profiler',
              value: solution.toLowerCase(),
            });
            break;
          default:
            break;
        }
      });
    }

    if (solutions.length > 1 && gridName !== 'RepBehavior') {
      solutions.splice(0, 0, { label: 'All', value: 'ALL' });
    }

    return solutions;
  }

  destroyGridSelections() {
    this.selectedDropdowns = {};
    this.selectedDates = {};
    this.selectedPresets = {};
  }
}
